import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, ExternalLink } from 'lucide-react'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@deepguide-ai/ui/tabs'
import type { ConfigModalProps, IntegrationProvider } from '../types'
import { IntegrationIcon } from '../icons'

function AzureDevOpsConfigModal({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  onComplete: () => void
}) {
  const [tab, setTab] = useState<'oauth' | 'pat'>('oauth')
  const [organization, setOrganization] = useState('')
  const [pat, setPat] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const invalidateWorkspaceIntegrations = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
    })

  const handleOAuth = async () => {
    setIsSaving(true)
    try {
      const baseWebUrl = import.meta.env.VITE_WEB_URL ?? window.location.origin
      const redirectUrl = `${baseWebUrl}/auth-callback/AZURE_DEVOPS`
      const result = await trpcClient.integrations.initAzureDevOpsAuth.mutate({ redirectUrl })

      openOAuthConnectPopup({
        authUrl: result.authUrl, windowName: 'azure-devops-oauth', provider: 'AZURE_DEVOPS',
        onSuccess: async () => {
          if (organization.trim()) {
            try {
              await trpcClient.integrations.azureDevOps.setOrganization.mutate({ organization: organization.trim() })
            } catch (err) {
              console.error('Failed to persist Azure DevOps organization:', err)
            }
          }
          await invalidateWorkspaceIntegrations()
          toast.success('Azure DevOps connected')
          onOpenChange(false)
          onComplete()
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: () => setIsSaving(false),
      })
    } catch (error) {
      console.error('Error initiating Azure DevOps OAuth:', error)
      toast.error('Failed to start Azure DevOps OAuth. Please try again.')
      setIsSaving(false)
    }
  }

  const handlePat = async () => {
    if (!organization.trim()) {
      toast.error('Organization slug is required')
      return
    }
    if (!pat.trim()) {
      toast.error('Personal access token is required')
      return
    }
    setIsSaving(true)
    try {
      await trpcClient.integrations.azureDevOps.createWithPat.mutate({
        organization: organization.trim(),
        pat: pat.trim(),
      })
      await invalidateWorkspaceIntegrations()
      toast.success('Azure DevOps connected')
      onOpenChange(false)
      onComplete()
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to connect Azure DevOps',
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IntegrationIcon type="AZURE_DEVOPS" />
            Connect Azure DevOps
          </DialogTitle>
          <DialogDescription>
            Connect Azure DevOps to scan branch policies, pipelines, service connections, and work items.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'oauth' | 'pat')} className="px-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="oauth">OAuth (Entra ID)</TabsTrigger>
            <TabsTrigger value="pat">Personal Access Token</TabsTrigger>
          </TabsList>

          <TabsContent value="oauth" className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ado-org-oauth">Organization slug (optional)</Label>
              <Input
                id="ado-org-oauth"
                autoComplete="off"
                spellCheck={false}
                placeholder="contoso (https://dev.azure.com/<slug>)"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
              <p className="text-[12px] text-text-subtle">
                You can leave this blank and pick the organization after sign-in.
              </p>
            </div>
            <a
              href="https://learn.microsoft.com/en-us/azure/devops/integrate/get-started/authentication/oauth"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-cobalt-strong hover:underline"
            >
              About Azure DevOps OAuth via Entra ID
              <ExternalLink className="h-3 w-3" />
            </a>
          </TabsContent>

          <TabsContent value="pat" className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ado-org-pat">Organization slug</Label>
              <Input
                id="ado-org-pat"
                autoComplete="off"
                spellCheck={false}
                placeholder="contoso"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ado-pat">Personal access token</Label>
              <Input
                id="ado-pat"
                type="password"
                autoComplete="new-password"
                placeholder="Azure DevOps PAT (Read scopes)"
                value={pat}
                onChange={(e) => setPat(e.target.value)}
              />
              <p className="text-[12px] text-text-subtle">
                Required scopes: Code (Read), Build (Read), Service Connections (Read), Work Items (Read), Project & Team (Read).
              </p>
            </div>
            <a
              href="https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] text-cobalt-strong hover:underline"
            >
              How to create a PAT
              <ExternalLink className="h-3 w-3" />
            </a>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={tab === 'oauth' ? handleOAuth : handlePat} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AzureDevOpsConfigModalWrapper(props: ConfigModalProps) {
  return (
    <AzureDevOpsConfigModal
      open={props.open}
      onOpenChange={props.onOpenChange}
      workspaceId={props.workspaceId}
      onComplete={props.onComplete}
    />
  )
}

export const AzureDevOpsProvider: IntegrationProvider = {
  id: 'AZURE_DEVOPS',
  name: 'Azure DevOps',
  description:
    'Branch policies, pipeline gates, service connections, and work items.',
  Icon: () => <IntegrationIcon type="AZURE_DEVOPS" />,
  ConfigModal: AzureDevOpsConfigModalWrapper,

  // Surface the OAuth/PAT chooser dialog. We immediately resolve `onSuccess`
  // so the integrations page opens our ConfigModal — the dialog itself
  // decides whether to launch OAuth or save a PAT.
  handleConnect: async ({ onSuccess, onFinish }) => {
    onSuccess?.({ provider: 'AZURE_DEVOPS' })
    onFinish?.('success')
  },

  onConnectComplete: async ({ code, state }) => {
    return trpcClient.integrations.completeAzureDevOpsAuth.mutate({ code, state })
  },
}
